import { ref, computed, unref, watch, type Ref } from 'vue';
import { CustomFormsFeatures, isValidCustomField } from '@nova/core';
import type { ICustomField, ITrigger, IFormField, IWarehouse } from '@nova/core';
import { useCustomForms } from '@satellite/composables/useCustomForms';

import type { CompleteCheckoutCustomFormEntry } from '@/services/checkoutService';

/** Form field that may have an order for sorting (e.g. from flow formFields). */
export type FormFieldWithOptionalOrder = IFormField & { order?: number };

export interface CheckoutFormFieldWithTrigger {
  formField: FormFieldWithOptionalOrder;
  triggerId: string;
  trigger: ITrigger;
}

export interface UseCheckoutCustomFormsOptions {
  warehouse: Ref<Partial<IWarehouse>> | Partial<IWarehouse>;
  assetVisitId: Ref<string> | string;
  onNotify?: (message: string, type: 'success' | 'warning' | 'error') => void;
}

function getFormFieldOrder(field: FormFieldWithOptionalOrder): number {
  return field.order ?? 0;
}

function collectFormFieldsWithTrigger(triggers: ITrigger[]): CheckoutFormFieldWithTrigger[] {
  const out: CheckoutFormFieldWithTrigger[] = [];

  const pushFields = (trigger: ITrigger, formFields?: readonly IFormField[]) => {
    if (!formFields?.length) {
      return;
    }

    for (const formField of formFields) {
      out.push({
        formField: formField as FormFieldWithOptionalOrder,
        triggerId: trigger.id,
        trigger
      });
    }
  };

  for (const trigger of triggers) {
    const flow = trigger.flow;
    if (!flow) {
      continue;
    }

    pushFields(trigger, flow.formFrom?.formFields);
    pushFields(trigger, flow.formTo?.formFields);
  }

  return out;
}

export function useCheckoutCustomForms(options: UseCheckoutCustomFormsOptions) {
  const warehouseRef = () => unref(options.warehouse);
  const assetVisitIdRef = () => unref(options.assetVisitId);

  const checkoutTriggers = computed<ITrigger[]>(() => {
    const warehouse = warehouseRef();
    const triggers = warehouse?.triggers ?? [];
    return triggers.filter(
      (t): t is ITrigger => t?.feature === CustomFormsFeatures.CheckOut && Boolean(t?.flow)
    );
  });

  const hasCheckoutCustomForms = computed(() => checkoutTriggers.value.length > 0);

  const timezone = computed(() => warehouseRef()?.timezone ?? '');
  const militaryTimeEnabled = computed(
    () => Boolean(warehouseRef()?.settings?.militaryTimeEnabled) ?? false
  );

  const triggersRef = ref<ITrigger[] | undefined>([]);
  watch(
    checkoutTriggers,
    v => {
      triggersRef.value = v;
    },
    { immediate: true }
  );

  const customForms = useCustomForms({
    triggers: triggersRef.value,
    timezone: timezone.value,
    militaryTimeEnabled: militaryTimeEnabled.value,
    onNotify: options.onNotify
  });

  const formFieldsWithTrigger = computed<CheckoutFormFieldWithTrigger[]>(() => {
    const out = collectFormFieldsWithTrigger(checkoutTriggers.value);
    out.sort((a, b) => getFormFieldOrder(a.formField) - getFormFieldOrder(b.formField));
    return out;
  });

  const formValues = ref<Record<string, unknown>>({});

  watch(
    () => assetVisitIdRef(),
    () => {
      formValues.value = {};
    },
    { immediate: true }
  );

  const setFormFieldValue = (formFieldId: string, value: unknown) => {
    formValues.value = { ...formValues.value, [formFieldId]: value };
  };

  const getFormFieldValue = (formFieldId: string): unknown => {
    return formValues.value[formFieldId];
  };

  const isFormValid = computed(() => {
    for (const { formField } of formFieldsWithTrigger.value) {
      const required = formField.required ?? false;
      const value = formValues.value[formField.id];
      const customField = customForms.getCustomField(formField);

      if (required) {
        const isEmpty =
          value === undefined ||
          value === null ||
          value === '' ||
          (Array.isArray(value) && value.length === 0);
        if (isEmpty) {
          return false;
        }
      }
      if (value !== undefined && value !== null && value !== '') {
        const fieldForValidation: ICustomField = {
          ...customField,
          value: value as ICustomField['value']
        };
        if (!isValidCustomField(fieldForValidation)) {
          return false;
        }
      }
    }
    return true;
  });

  /*
   * Only formFieldId/triggerId/value are sent. objectId, entityName and warehouseId
   * are derived server-side by both checkout paths -- the BFF from the :warehouseId
   * path param, dto.assetVisitId and trigger.dataEntityName, and the legacy
   * CheckoutCustomFormsInterceptor from request.params plus its own persistData
   * arguments. The legacy endpoint silently dropped the extra keys, but
   * POST /bff/juno/:warehouseId validates with forbidNonWhitelisted and rejected
   * them with a 400.
   */
  const prepareSubmitPayload = (): CompleteCheckoutCustomFormEntry[] => {
    if (!warehouseRef()?.id || !assetVisitIdRef()) {
      return [];
    }

    return formFieldsWithTrigger.value.map(({ formField, triggerId }) => ({
      formFieldId: formField.id,
      triggerId,
      value: formValues.value[formField.id]
    }));
  };

  return {
    checkoutTriggers,
    hasCheckoutCustomForms,
    formFieldsWithTrigger,
    formValues,
    getFormFieldValue,
    setFormFieldValue,
    getCustomField: customForms.getCustomField,
    customFormsComponentsMapping: customForms.customFormsComponentsMapping,
    isFormValid,
    prepareSubmitPayload,
    timezone,
    militaryTimeEnabled
  };
}
